import { useEffect, useState, useCallback, useRef } from "react"
import { Dialog, DialogContent } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { getRecentPatients, getUpcomingAppointments, searchPatients } from "@/lib/api"
import { displayAge } from "@/lib/patientAge"
import { useNavigate } from "react-router-dom"
import { useAuth } from "@/context/AuthContext"
import { usePermission } from "@/hooks/usePermission"
import AddAppointmentModal from "@/components/Appointment/AddAppointmentModal"
import {
  Search,
  User,
  Calendar,
  Phone,
  Loader2,
  AlertCircle,
  ArrowRight,
  History,
  CalendarDays,
  UserPlus
} from "lucide-react"
import { cn } from "@/lib/utils"
import { toast } from "@/hooks/use-toast"
import NewPatientModal from "@/components/Patient/NewPatientModal"

type SearchResult = {
  id: string
  type: 'patient' | 'appointment'
  name: string
  subtitle: string
  metadata?: string
  badge?: string
}

type Props = {
  open: boolean
  onClose: () => void
}

function debounce<T extends (...args: any[]) => any>(
    func: T,
    wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout
  return (...args: Parameters<T>) => {
    clearTimeout(timeout)
    timeout = setTimeout(() => func(...args), wait)
  }
}

// ── Sub-components defined outside to avoid remount on every render ────────────

function QuickActionButton({ icon: Icon, label, action, variant = "outline" }: {
  icon: any
  label: string
  action: () => void
  variant?: "outline" | "default"
}) {
  return (
    <Button variant={variant} size="sm" onClick={action} className="flex items-center gap-2 h-8">
      <Icon className="w-3.5 h-3.5" />
      {label}
    </Button>
  )
}

function SearchResultItem({ result, index, isSelected, onSelect }: {
  result: SearchResult
  index: number
  isSelected: boolean
  onSelect: (index: number) => void
}) {
  return (
    <div
      onClick={() => onSelect(index)}
      className={cn(
        "flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors group border",
        isSelected ? "bg-primary/5 border-primary/20" : "hover:bg-muted/50 border-transparent"
      )}
    >
      <div className={cn(
        "w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0",
        result.type === 'patient' ? "bg-primary/10 text-primary" : "bg-success/10 text-success"
      )}>
        {result.type === 'patient' ? <User className="w-3.5 h-3.5" /> : <Calendar className="w-3.5 h-3.5" />}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <span className="font-medium text-foreground truncate text-sm">{result.name}</span>
          {result.badge && (
            <Badge variant="secondary" className="text-[10px] px-1.5 py-0 flex-shrink-0">{result.badge}</Badge>
          )}
        </div>
        <p className="text-xs text-muted-foreground truncate">{result.subtitle}</p>
        {result.metadata && (
          <p className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
            <Phone className="w-2.5 h-2.5" />
            {result.metadata}
          </p>
        )}
      </div>

      <ArrowRight className={cn(
        "w-3.5 h-3.5 text-muted-foreground flex-shrink-0",
        isSelected ? "text-primary" : "group-hover:text-foreground"
      )} />
    </div>
  )
}

function EmptyState({ icon: Icon, title, description, action }: {
  icon: any
  title: string
  description: string
  action?: { label: string; onClick: () => void }
}) {
  return (
    <div className="flex flex-col items-center justify-center py-8 text-center">
      <div className="w-9 h-9 rounded-full bg-muted/40 flex items-center justify-center mb-2">
        <Icon className="w-4 h-4 text-muted-foreground" />
      </div>
      <p className="text-sm font-medium text-foreground mb-0.5">{title}</p>
      <p className="text-xs text-muted-foreground mb-3 max-w-[180px]">{description}</p>
      {action && <QuickActionButton icon={UserPlus} label={action.label} action={action.onClick} variant="default" />}
    </div>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

export default function SearchOverlay({ open, onClose }: Props) {
  const { currentUser } = useAuth()
  const { can } = usePermission()
  // Front-desk view if user has no clinical profile (doctor_id), regardless of role
  const isFrontDesk = !currentUser?.doctor_id;
  const canViewAppointments = can('appointments:read')
  const canManageAppointments = can('appointments:write')
  const canViewPatients = can('patients:read')
  const canManagePatients = can('patients:write')
  const [query, setQuery] = useState("")
  const [results, setResults] = useState<SearchResult[]>([])
  const [recentPatients, setRecentPatients] = useState<any[]>([])
  const [upcomingAppointments, setUpcomingAppointments] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [defaultsLoading, setDefaultsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [selectedIndex, setSelectedIndex] = useState(-1)
  const [showNewPatientModal, setShowNewPatientModal] = useState(false)
  const [showAddAppointmentModal, setShowAddAppointmentModal] = useState(false)
  const navigate = useNavigate()
  const inputRef = useRef<HTMLInputElement>(null)

  const debouncedSearch = useCallback(
    debounce(async (searchQuery: string) => {
      if (searchQuery.trim().length === 0) {
        setResults([])
        return
      }

      setLoading(true)
      setError(null)

      try {
        const searchResults = await searchPatients(searchQuery, currentUser?.clinic_id)

        const transformedResults: SearchResult[] = (searchResults || [])
          .slice(0, 10)
          .map((patient: any) => ({
            id: patient.id,
            type: 'patient' as const,
            name: patient.name || '—',
            subtitle: [displayAge(patient.age, patient.date_of_birth) || null, patient.gender || null].filter(Boolean).join(' · ') || '—',
            metadata: patient.phone || undefined,
            badge: patient.lastVisit ? 'Recent' : undefined
          }))

        setResults(transformedResults)
        setSelectedIndex(-1)
      } catch (err) {
        console.error("Search failed:", err)
        setError("Search failed. Please check your connection.")
        setResults([])
      } finally {
        setLoading(false)
      }
    }, 300),
    [currentUser?.clinic_id]
  )

  useEffect(() => {
    if (!open) {
      setQuery("")
      setResults([])
      setError(null)
      setSelectedIndex(-1)
    } else {
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  useEffect(() => {
    debouncedSearch(query)
  }, [query, debouncedSearch])

  useEffect(() => {
    const loadDefaults = async () => {
      if (!open || !currentUser?.clinic_id) return
      setDefaultsLoading(true)
      try {
        const [patientsRes, appointmentsRes] = await Promise.all([
          canViewPatients ? getRecentPatients(5, currentUser.doctor_id ?? undefined, currentUser.clinic_id) : Promise.resolve([]),
          canViewAppointments ? getUpcomingAppointments(5, currentUser.doctor_id ?? undefined, currentUser.clinic_id) : Promise.resolve([])
        ])
        setRecentPatients(Array.isArray(patientsRes) ? patientsRes : [])
        setUpcomingAppointments(appointmentsRes || [])
      } catch (e) {
        console.error("Failed to load defaults:", e)
      } finally {
        setDefaultsLoading(false)
      }
    }
    loadDefaults()
  }, [open, currentUser?.doctor_id, currentUser?.clinic_id, canViewPatients, canViewAppointments])

  useEffect(() => {
    setSelectedIndex(-1)
  }, [results.length, recentPatients.length, upcomingAppointments.length])

  const handleSelection = useCallback((index: number) => {
    if (results.length > 0) {
      const result = results[index]
      if (result?.type === 'patient') {
        navigate(isFrontDesk ? `/frontdesk/patients/${result.id}` : `/patients/${result.id}`)
      }
    } else {
      const allDefaults = [...upcomingAppointments, ...recentPatients]
      const item = allDefaults[index]
      if (item) {
        const patientId = item.patient_id || item.id
        navigate(isFrontDesk ? `/frontdesk/patients/${patientId}` : `/patients/${patientId}`)
      }
    }
    onClose()
  }, [results, recentPatients, upcomingAppointments, navigate, onClose, isFrontDesk])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!open) return

      const totalItems = results.length > 0
        ? results.length
        : (recentPatients.length + upcomingAppointments.length)

      if (totalItems === 0) return

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault()
          setSelectedIndex(prev => (prev + 1) % totalItems)
          break
        case 'ArrowUp':
          e.preventDefault()
          setSelectedIndex(prev => prev <= 0 ? totalItems - 1 : prev - 1)
          break
        case 'Enter':
          e.preventDefault()
          if (selectedIndex >= 0 && selectedIndex < totalItems) handleSelection(selectedIndex)
          break
        case 'Escape':
          e.preventDefault()
          onClose()
          break
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, results.length, recentPatients.length, upcomingAppointments.length, selectedIndex, onClose, handleSelection])

  const goToPatient = (id: string) => {
    navigate(isFrontDesk ? `/frontdesk/patients/${id}` : `/patients/${id}`)
    onClose()
  }

  const handleNewPatient = () => { onClose(); setShowNewPatientModal(true) }
  const handleAddAppointment = () => { onClose(); setShowAddAppointmentModal(true) }

  const formatTime = (time: string) => {
    const [h, m] = time.split(':').map(Number)
    const d = new Date()
    d.setHours(h, m)
    return d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="max-w-2xl p-0 gap-0 h-[70vh] overflow-hidden flex flex-col">

          {/* Search input — header */}
          <div className="p-3 border-b flex-shrink-0">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="search-overlay-input"
                name="search-overlay-input"
                ref={inputRef}
                placeholder="Search patients by name or phone..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-9 pr-4 h-9 text-sm border-0 bg-transparent focus-visible:ring-0 focus-visible:ring-offset-0"
              />
              {loading && (
                <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
              )}
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto min-h-0">

            {error && (
              <div className="m-3 flex items-center gap-2 p-3 bg-destructive/10 text-destructive rounded-lg text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                {error}
              </div>
            )}

            {/* Search results */}
            {query.trim().length > 0 && !error && (
              <div className="p-3">
                {loading ? (
                  <div className="space-y-2">
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="flex items-center gap-2 p-1.5">
                        <div className="w-7 h-7 bg-muted rounded-full animate-pulse flex-shrink-0" />
                        <div className="flex-1 space-y-1.5">
                          <div className="h-3 bg-muted rounded animate-pulse w-3/4" />
                          <div className="h-2 bg-muted rounded animate-pulse w-1/2" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : results.length === 0 ? (
                  <EmptyState
                    icon={Search}
                    title="No results found"
                    description="Try different terms or add a new patient"
                    action={canManagePatients ? { label: "Add New Patient", onClick: handleNewPatient } : undefined}
                  />
                ) : (
                  <div className="space-y-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs font-medium text-muted-foreground">
                        {results.length} result{results.length !== 1 ? 's' : ''}
                      </span>
                      {results.length >= 10 && (
                        <span className="text-[10px] text-muted-foreground">Showing first 10</span>
                      )}
                    </div>
                    {results.map((result, index) => (
                      <SearchResultItem
                        key={result.id}
                        result={result}
                        index={index}
                        isSelected={selectedIndex === index}
                        onSelect={handleSelection}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Default view */}
            {query.trim().length === 0 && !error && (canViewAppointments || canViewPatients) && (
              <div className="p-3 h-full">
                <div className={cn("grid gap-4 h-full", canViewAppointments && canViewPatients ? "grid-cols-2" : "grid-cols-1")}>

                  {/* Upcoming appointments */}
                  {canViewAppointments && (
                    <div className="flex flex-col gap-1.5 min-h-0">
                      <h3 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5 flex-shrink-0">
                        <CalendarDays className="w-3.5 h-3.5" />
                        Upcoming Appointments
                      </h3>
                      <div className="space-y-1 overflow-y-auto flex-1">
                        {defaultsLoading ? (
                          [...Array(3)].map((_, i) => (
                            <div key={i} className="flex items-center gap-2 p-1.5">
                              <div className="w-7 h-7 bg-muted rounded-full animate-pulse flex-shrink-0" />
                              <div className="flex-1 space-y-1.5">
                                <div className="h-3 bg-muted rounded animate-pulse w-3/4" />
                                <div className="h-2 bg-muted rounded animate-pulse w-1/2" />
                              </div>
                            </div>
                          ))
                        ) : upcomingAppointments.length > 0 ? (
                          upcomingAppointments.map((appt, index) => (
                            <div
                              key={appt.id}
                              onClick={() => goToPatient(appt.patient_id)}
                              className={cn(
                                "flex items-center gap-2 p-1.5 rounded-lg cursor-pointer transition-colors group",
                                selectedIndex === index
                                  ? "bg-primary/5"
                                  : "hover:bg-muted/50"
                              )}
                            >
                              <div className="w-7 h-7 bg-primary/10 text-primary rounded-full flex items-center justify-center flex-shrink-0">
                                <Calendar className="w-3.5 h-3.5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-foreground truncate">{appt.patient_name}</p>
                                <p className="text-xs text-muted-foreground">
                                  {new Date(appt.date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                                  {appt.time ? ` at ${formatTime(appt.time)}` : ''}
                                </p>
                                {appt.reason && (
                                  <p className="text-[10px] text-muted-foreground truncate">{appt.reason}</p>
                                )}
                              </div>
                              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 group-hover:text-foreground" />
                            </div>
                          ))
                        ) : (
                          <EmptyState
                            icon={Calendar}
                            title="No upcoming appointments"
                            description="Schedule new appointments for your patients"
                            action={canManageAppointments ? { label: "New Appointment", onClick: handleAddAppointment } : undefined}
                          />
                        )}
                      </div>
                    </div>
                  )}

                  {/* Recent patients */}
                  {canViewPatients && (
                    <div className="flex flex-col gap-1.5 min-h-0">
                      <h3 className="text-xs font-medium text-muted-foreground flex items-center gap-1.5 flex-shrink-0">
                        <History className="w-3.5 h-3.5" />
                        Recent Patients
                      </h3>
                      <div className="space-y-1 overflow-y-auto flex-1">
                        {defaultsLoading ? (
                          [...Array(3)].map((_, i) => (
                            <div key={i} className="flex items-center gap-2 p-1.5">
                              <div className="w-7 h-7 bg-muted rounded-full animate-pulse flex-shrink-0" />
                              <div className="flex-1 space-y-1.5">
                                <div className="h-3 bg-muted rounded animate-pulse w-2/3" />
                                <div className="h-2 bg-muted rounded animate-pulse w-1/3" />
                              </div>
                            </div>
                          ))
                        ) : recentPatients.length > 0 ? (
                          recentPatients.map((patient, index) => (
                            <div
                              key={patient.id}
                              onClick={() => goToPatient(patient.id)}
                              className={cn(
                                "flex items-center gap-2 p-1.5 rounded-lg cursor-pointer transition-colors group border",
                                selectedIndex === index + upcomingAppointments.length
                                  ? "bg-primary/5 border-primary/20"
                                  : "hover:bg-muted/50 border-transparent"
                              )}
                            >
                              <div className="w-7 h-7 bg-primary/10 text-primary rounded-full flex items-center justify-center flex-shrink-0">
                                <User className="w-3.5 h-3.5" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm text-foreground truncate">{patient.name || '—'}</p>
                                <p className="text-xs text-muted-foreground">
                                  {[displayAge(patient.age, patient.date_of_birth) || null, patient.gender || null].filter(Boolean).join(' · ') || '—'}
                                </p>
                                {patient.phone && (
                                  <p className="text-[10px] text-muted-foreground flex items-center gap-1 mt-0.5">
                                    <Phone className="w-2.5 h-2.5" />
                                    {patient.phone}
                                  </p>
                                )}
                              </div>
                              <ArrowRight className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0 group-hover:text-foreground" />
                            </div>
                          ))
                        ) : (
                          <EmptyState
                            icon={User}
                            title="No recent patients"
                            description="Add new patients to your practice"
                            action={canManagePatients ? { label: "New Patient", onClick: handleNewPatient } : undefined}
                          />
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer — keyboard hints */}
          <div className="px-3 py-2 border-t bg-muted/20 flex-shrink-0">
            <div className="flex items-center justify-between text-[10px] text-muted-foreground">
              <div className="flex items-center gap-3">
                <span className="flex items-center gap-1">
                  <kbd className="px-1 py-0.5 bg-background border rounded text-[9px]">↑↓</kbd>
                  Navigate
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1 py-0.5 bg-background border rounded text-[9px]">↵</kbd>
                  Select
                </span>
                <span className="flex items-center gap-1">
                  <kbd className="px-1 py-0.5 bg-background border rounded text-[9px]">Esc</kbd>
                  Close
                </span>
              </div>
              <span>⌘K to open</span>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <NewPatientModal
        isOpen={showNewPatientModal}
        onClose={() => setShowNewPatientModal(false)}
        onPatientCreated={() => setShowNewPatientModal(false)}
        title="Add New Patient"
      />

      <AddAppointmentModal
        isOpen={showAddAppointmentModal}
        onClose={() => setShowAddAppointmentModal(false)}
        patients={recentPatients}
        onAdded={() => {
          setShowAddAppointmentModal(false)
          toast({ title: "Appointment Created", description: "New appointment has been scheduled successfully." })
        }}
        showPatientSelection={true}
        title="New Appointment"
        description="Schedule a new patient appointment"
      />
    </>
  )
}
